import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpInterceptorFn,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConnectionTimeoutError } from '@interacta-shared/util';
import { Observable, pipe, range, throwError, timer, zip } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';

const retryStatus = [429, 502];
const errorStatus = 500;
const timeoutStatus = 502;
// Load balancer timeout is a server-side config we don't control (60s prod /
// 30s dev,test,cert). FE fixes this threshold at 60s; a 502 whose cumulative
// duration reaches it is treated as an LB timeout rather than a transient bad
// gateway.
const connectionTimeoutThresholdMs = 60_000;
const interactaBackendHeaders = [
    'interacta-backend-version',
    'xlearning-backend-version',
];
const excludedApis = [
    /internal\/v2\/communication\/posts\/data\/communities\/\d+\/export$/,
    /\/core\/user-profile\/info\/private-email\/force-verification$/,
];

function backoff(maxTries: number, ms: number, startTime: number) {
    return pipe(
        retryWhen<HttpEvent<unknown>>((attempts) =>
            zip(range(1, maxTries + 1), attempts).pipe(
                mergeMap(([i, a]: [number, HttpResponse<unknown>]) => {
                    if (
                        a.status === timeoutStatus &&
                        Date.now() - startTime >= connectionTimeoutThresholdMs
                    )
                        // The backend keeps processing after the cut, so the
                        // operation may still complete: signal a timeout
                        // instead of retrying (avoids re-submitting it).
                        return throwError(() => new ConnectionTimeoutError());
                    if (
                        i <= maxTries &&
                        (retryStatus.includes(a.status) || // if 429 OR
                            (a.status === errorStatus &&
                                !interactaBackendHeaders.some((appHeader) =>
                                    a.headers?.has(appHeader),
                                ))) && // 500 not generated by interacta-backend
                        (excludedApis.length === 0 ||
                            excludedApis.every(
                                (api) => a.url == null || !api.test(a.url),
                            ))
                    )
                        return timer(i * i * ms);
                    else return throwError(() => a);
                }),
            ),
        ),
    );
}

@Injectable()
export class ExponentialBackoffInterceptor implements HttpInterceptor {
    intercept(
        req: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<any>> {
        const startTime = Date.now();
        return next.handle(req).pipe(backoff(4, 250, startTime));
    }
}

export const exponentialBackoffInterceptor: HttpInterceptorFn = (req, next) => {
    return inject(ExponentialBackoffInterceptor).intercept(req, {
        handle: next,
    });
};
